<template>
  <div class="main-content">
    <!-- Page Header Start -->
    <PageHeader>
      <PageHeaderContent
        :pageTitle="pageTitle"
        :subText="`Project Spectrum offers comprehensive courses from CMMC Levels and FOCI through Encite. Explore our resources to learn how they play a vital role in enhancing your organization's cybersecurity posture.<br><br>To access Project Spectrum's online courses, you must log into the Encite learning management platform.`"
        :route="'online-courses'"
        :backButtonText="'Back To All Online Courses'"
        :desktopSubTitleWidth="'45%'"
        :mobileSubTitleWidth="'85%'"
      />
    </PageHeader>
    <!-- Page Header End -->

    <!-- Blog Grid Section Start -->
    <section class="section p-0 mt-5 mb-4 section-content">
      <div class="container-fluid px-xs-1">
        <div v-if="!isLoggedIn" class="my-2 d-flex align-items-center" style="min-height: 200px;">
          <p class="access-text">
            {{ `To access the ${courseTitle} courses you must first be logged into the Online Courses page` }}.
          </p>
        </div>
        <!-- Encite Courses -->
        <div v-else class="d-flex flex-column">
          <div class="d-flex justify-content-center mb-4">
            <div style="width:90%" class="d-flex flex-column justify-content-center align-items-center">
              <p class="m-0 sub-title-text">START LEARNING</p>
              <p class="m-0 main-title-text">{{ courseFullTitle ?? courseTitle }}</p>
            </div>
          </div>

          <!-- <div v-if="!(courseFullTitle || courseTitle || '').toLowerCase().includes('cmmc level 1')" class="container mb-4">
            <EnciteCourseCredential />
          </div> -->
          <div class="container mb-4">
            <EnciteCourseCredential />
          </div>

          <div class="information-card-container">
            <InformationCard
              v-for="item in displayedItems"
              :key="item.id"
              :title="item.name"
              :description="item.description"
              :backgroundColor="informationCardColor"
              :textColor="informationCardTextColor"
              :buttonTitle="'Start'"
              :toLink="item.link"
            />
          </div>

          <div v-if="totalRows > perPage">
            <b-pagination
              id="blog-pagination"
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              align="center"
              @page-click="pageClicked"
            ></b-pagination>
          </div>
        </div>
      </div>
    </section>
    <!-- Blog Grid Section End -->
  </div>
</template>
<script>
import axios from "axios"
import { BPagination, BFormInput } from "bootstrap-vue"
import { isLoggedIn, handleApiError } from "../utils/authentication"
import PageHeader from "./library/widgets/page-header.vue";
import PageHeaderContent from "./library/widgets/page-header-content.vue";
import InformationCard from "./library/widgets/information-card.vue";
import EnciteCourseCredential from "./library/widgets/encite-course-credential.vue";
import { EventBus } from "../utils/eventBus";

export default {
  name: "encite-courses",
  components: {
    BPagination,
    BFormInput,
    PageHeader,
    PageHeaderContent,
    InformationCard,
    EnciteCourseCredential,
  },
  props: {
    pageTitle: String,
    courseTitle: String,
    courseFullTitle: String,
    courseItems: Array,
    informationCardColor: String,
    informationCardTextColor: String,
  },
  data() {
    return {
      isLoggedIn: isLoggedIn(),
      username: "Loading...",
      password: "Loading...",
      enciteCredentialError: false,
      showPassword: false,
      searchQuery: this.query,
      items: [],
      currentPage: 1,
      perPage: 6,
    }
  },
  computed: {
    itemsFromSearchQuery() {
      if (this.searchQuery) {
        return this.courseItems.filter((item) => {
          return this.searchQuery
            .toLowerCase()
            .split(" ")
            .every(
              (v) =>
                item.name.toLowerCase().includes(v) ||
                item.description.toLowerCase().includes(v)
            )
        })
      } else {
        return this.courseItems;
      }
    },
    displayedItems() {
      return this.itemsFromSearchQuery.slice(
        (this.currentPage - 1) * this.perPage,
        this.currentPage * this.perPage
      );
    },
    totalRows() {
      return this.itemsFromSearchQuery.length;
    },
  },
  mounted: async function () {
    EventBus.$emit("activate-indicated-menu-item", "Courses/Videos");
  },
  methods: {
    pageClicked() {
      window.scrollTo({ top: 0, behavior: "smooth" })
    },
    // This method is used to handle the encite link to send users details to the new encite platform which is not being used currently as CMMC Level 1 and CMMC Level 2 courses are not available on the new encite platform made by Nilanjans Team
    getAuthenticatedLink(originalLink) {
      // Check if this is a student.encite.io link - only process these
      if (!originalLink.includes('student.encite.io')) {
        // For non-student.encite.io links, return the original link unchanged
        return originalLink;
      }
      // Get Vuex store data to extract authentication tokens
      let vuexData;
      try {
        vuexData = JSON.parse(localStorage.getItem('vuex'));
      } catch (error) {
        console.error('Error parsing Vuex data:', error);
        // If we can't get the auth tokens, just return the original link
        return originalLink;
      }
      
      // Extract the tokens from Vuex store
      const accessToken = vuexData.accessToken;
      const idToken = vuexData.idToken;
      const refreshToken = vuexData.refreshToken;
      const userObject = vuexData.user;
      
      // Check if we have the required tokens
      if (!accessToken || !idToken || !refreshToken || !userObject) {
        // If auth tokens are missing, just return the original link
        return originalLink;
      }
      
      try {
        // Encode the user object for URL
        const encodedUser = encodeURIComponent(JSON.stringify(userObject));
        
        // Extract the course ID from the original link
        let courseId = '';
        let redirectPath = '';
        
        // Handle student.encite.io links
        if (originalLink.includes('student.encite.io/course-catalog/')) {
          const parts = originalLink.split('/');
          courseId = parts[parts.length - 1];
          redirectPath = `course/${courseId}`;
        } else {
          // For other links, use the entire path as redirect
          // Remove the domain part and get everything after the first slash
          const urlObj = new URL(originalLink);
          redirectPath = urlObj.pathname + urlObj.search;
          if (redirectPath.startsWith('/')) {
            redirectPath = redirectPath.substring(1);
          }
        }
        
        // Build the handoff URL
        let baseHandoffUrl = 'https://student.encite.io/handoff';
        
        // Create the auth fragment with redirect
        let authFragment = `#accessToken=${accessToken}&idToken=${idToken}&refreshToken=${refreshToken}`;
        
        // Add redirect if we have a path
        if (redirectPath) {
          authFragment += `&redirect=${encodeURIComponent(redirectPath)}`;
        }
        
        // Add user info
        authFragment += `&user=${encodedUser}`;
        
        // Return the complete handoff URL with auth tokens
        return baseHandoffUrl + authFragment;
      } catch (error) {
        console.error('Error creating authenticated link:', error);
        return originalLink;
      }
    },
    // This method can be used to manually clear tokens (for testing)
    clearTokens() {
      let item = JSON.parse(localStorage.getItem('vuex'));
      if (item) {
        item['idToken'] = null;
        item['accessToken'] = null;
        item['refreshToken'] = null;
        item['user'] = null;
        item['isLoggedIn'] = false;
        localStorage.setItem('vuex', JSON.stringify(item));
      }
    }
  },
}
</script>
<style scoped>
#blog-pagination li {
  margin: 0px;
}

.sub-title-text {
  text-align: center;
  font-family: Rajdhani;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  color: #424EC4;
}

.main-title-text {
  text-align: center;
  font-family: GeomGraphic;
  font-size: clamp(22px, 2vw, 36px);
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  color: #000;
}

.access-text {
  text-align: center;
  font-family: GeomGraphic;
  font-size: clamp(22px, 2vw, 36px);
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  color: #000;
}
</style>